/**
 * The evaluator's settings, with the defaults it works on from the first day.
 *
 * OHC's brief (15 October 2026, point 6): agreed defaults that work without any setup;
 * adjustable lookback period, number of comparable sales, minimum sample size, purchase
 * discount and update frequency; a Reset to defaults button; the settings used saved
 * with each evaluation; and the purchase discount moving the maximum offer only, never
 * the market value.
 *
 * The purchase discounts and buying rules are D's own (19 October 2026): at a physical
 * trade show, buy back at 70 to 75% of market price; online, on Carousell or Telegram
 * groups, up to 90% when the card has value; and above all the card must have room to go
 * up, and must be possible to sell once it has. The condition discounts are starting
 * figures. Everything here can be changed on the Settings page.
 */
/** Where the card is being bought: D pays a different share of market price at each. */
export const PLACES = ["show", "online"];
export const PLACE_LABEL = { show: "Trade show", online: "Online" };
export const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"];
export const CONDITION_LABEL = {
    NM: "Near Mint",
    LP: "Lightly Played",
    MP: "Moderately Played",
    HP: "Heavily Played",
    DMG: "Damaged",
};
export const DEFAULT_SETTINGS = {
    lookbackDays: 60,
    comparables: 5,
    minSample: 3,
    purchaseDiscountShow: 30,
    purchaseDiscountOnline: 10,
    conditionDiscounts: { NM: 0, LP: 15, MP: 30, HP: 50, DMG: 70 },
    priceUpdateHours: 24,
    cardUpdateDays: 7,
    buyingRules: "At a physical trade show, where we deal with people face to face, we buy back at 70-75% of market price. " +
        "Online, on Carousell or Telegram groups, up to 90% if we think the card has value. " +
        "Most importantly, the card must have value to go up in price. Secondly, it must be possible to sell and liquidate it after it goes up.",
};
/** The limits each number must stay inside, shown beside the field and checked on save. */
export const SETTING_LIMITS = {
    lookbackDays: [7, 365],
    comparables: [1, 20],
    minSample: [1, 10],
    purchaseDiscountShow: [0, 90],
    purchaseDiscountOnline: [0, 90],
    conditionDiscount: [0, 95],
    priceUpdateHours: [6, 168],
    cardUpdateDays: [1, 60],
    buyingRulesLength: [0, 4000],
};
/** Everything wrong with a settings object, in words for the screen. Empty means fine. */
export function checkSettings(s) {
    const problems = [];
    const whole = (field, value, label) => {
        const [min, max] = SETTING_LIMITS[field];
        if (!Number.isInteger(value) || value < min || value > max) {
            problems.push({ field, message: `${label} must be a whole number from ${min} to ${max}` });
        }
    };
    whole("lookbackDays", s.lookbackDays, "Lookback days");
    whole("comparables", s.comparables, "Comparable sales");
    whole("minSample", s.minSample, "Minimum sample");
    whole("purchaseDiscountShow", s.purchaseDiscountShow, "Purchase discount at a trade show");
    whole("purchaseDiscountOnline", s.purchaseDiscountOnline, "Purchase discount online");
    whole("priceUpdateHours", s.priceUpdateHours, "Price update hours");
    whole("cardUpdateDays", s.cardUpdateDays, "New card check days");
    for (const c of CONDITIONS) {
        const v = s.conditionDiscounts[c];
        const [min, max] = SETTING_LIMITS.conditionDiscount;
        if (!Number.isInteger(v) || v < min || v > max) {
            problems.push({ field: `conditionDiscounts.${c}`, message: `${CONDITION_LABEL[c]} discount must be a whole number from ${min} to ${max}` });
        }
    }
    if (s.minSample > s.comparables) {
        problems.push({ field: "minSample", message: "Minimum sample cannot be more than the number of comparable sales" });
    }
    if (s.buyingRules.length > SETTING_LIMITS.buyingRulesLength[1]) {
        problems.push({ field: "buyingRules", message: `Buying rules can be up to ${SETTING_LIMITS.buyingRulesLength[1]} characters` });
    }
    return problems;
}
//# sourceMappingURL=settings.js.map